import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { saveAs } from 'file-saver';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as XLSX from 'xlsx';
import useMyMApi from '../../hooks/useMyMApi';
import useValidationRules from '../../hooks/useValidationRules';
import useWebContext from '../../hooks/useWebContext';
import { MyMButton, MyMSearchButton } from '../../ui-elements/MyMButton';
import { MyMDropDownList } from '../../ui-elements/MyMDropDownList';
import { MyMLabel, MyMVariantLabel } from '../../ui-elements/MyMLabel';
import { ValidatableCheckBoxList } from '../../ui-parts/ValidatableCheckBoxList';
import ValidatableDatePicker from '../../ui-parts/ValidatableDatePicker';
import ValidatableTextBox from '../../ui-parts/ValidatableTextBox';
import { mediaQuery, useMediaQuery } from '../../hooks/useMediaQuery';
import { utility } from '../../utility';
import dayjs from 'dayjs';

/** MyMine Version2.1 RequestHistory画面新規追加
* RequestHistorySearchCondition Component
*/
export const RequestHistorySearchCondition = React.memo((props) => {
  const { formState, control, handleSubmit, reset, trigger } = useForm({
    mode: 'onSubmit',
  })
  const validationRules = useValidationRules();
  const isPc = useMediaQuery(mediaQuery.pc);
  const {
    screenId,
    searchCondition,
    searchInputCondition,
    setSearchInputCondition,
    setPageNumber,
    setIsSearch,
    setIsLoading,
    defaultInputCondition
  } = props
  const { controlSettings, messageList } = useWebContext(screenId);
  const apiRequest = useMyMApi(screenId);

  /** 検索クリック時処理 */
  const handleSearch = () => {
    // ページ番号・ソート情報をリセットし、useEffectフックで検索実施
    setIsSearch(true);
    setPageNumber(1);
  }

  const onError = () => {
    window.alert(messageList.MyM_MSG_E_0003);
  };

  /** クリアクリック時処理 */
  const handleClear = () => {
    reset();
    setSearchInputCondition({ ...defaultInputCondition });
  };

  /** ダウンロードクリック時処理*/
  const handleDownload = async () => {

    setIsLoading(true);
    const queryParameters = { ...defaultInputCondition };
    queryParameters.LimitMaxRowCount = 999;

    await apiRequest.requestJson(
      `api/request/load-list?${utility.makeEncodeParameter(queryParameters)}`,
      'GET',
      (result) => {

        // 結果件数0件の場合はメッセージ表示
        if (result.Result.TotalRecordCount === 0) {
          alert(messageList.MyM_MSG_I_0059)
          setIsLoading(false);
          return;
        }

        const createWriteRowData = (requestItem) => {
          return {
            [controlSettings.LabelList.lblXlListUserName]: requestItem.Name,
            [controlSettings.LabelList.lblXlListEMail]: requestItem.Email,
            [controlSettings.LabelList.lblXlListCompany]: requestItem.CompanyName,
            [controlSettings.LabelList.lblXlListTargetProductCategory]: result.Condition.TargetProductCategoryList.find(x => x.Key === requestItem.TargetProductCategoryId).Value,
            [controlSettings.LabelList.lblXlListTargetProduct]: requestItem.TargetProductId.split(',').map(x => result.Condition.TargetProductList.find(y => y.Key.toString() === x).Value).join(','),
            [controlSettings.LabelList.lblXlListRequestCategory]: result.Condition.RequestCategoryList.find(x => x.Key === requestItem.RequestCategoryId).Value,
            [controlSettings.LabelList.lblXlListRequestTitle]: requestItem.RequestTitle,
            [controlSettings.LabelList.lblXlListRequestContent]: requestItem.RequestContent,
            [controlSettings.LabelList.lblXlListAttachemntFile]: requestItem.FileList.map(x => x.Value).join('|'),
            [controlSettings.LabelList.lblXlListDeadlineDate]: utility.formatDate(requestItem.DeadlineDate),
            [controlSettings.LabelList.lblXlListPostDate]: utility.formatDate(requestItem.RegistrationDate),
            [controlSettings.LabelList.lblXlListStatus]: result.Condition.RequestAnswerStatusList.find(x => x.Key === requestItem.AnswerStatus).Value,
            [controlSettings.LabelList.lblXlListSequencecOfEvents]: requestItem.Result,
          };
        }

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(result.Result.ItemList.map(createWriteRowData));

        // 列幅設定
        ws['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 25 }, { wch: 35 }, { wch: 60 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 50 },];

        XLSX.utils.book_append_sheet(wb, ws, controlSettings.LabelList.lblXlRequestListSheetName);

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `MyMSuggestionList_${utility.formatDateTime(new Date(), 'yyyyMMddHHmmss')}.xlsx`)
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      });
  };

  const conditionControlList = [
    {
      xl: 4,
      label: controlSettings.LabelList.lblTitleDetailLabel,
      control: (
        <ValidatableTextBox
          control={control}
          name="titleOrDetail"
          defaultValue={searchInputCondition.titleOrDetail}
          formState={formState}
          maxLength={200}
          rules={{
            ...validationRules.maxLength(200),
          }}
          onBlur={(e) => setSearchInputCondition({ ...searchInputCondition, [e.target.name]: e.target.value })}
          width='100%'
        />
      ),
    },

    {
      xl: 4.5,
      label: controlSettings.LabelList.lblPostDateLabel,
      control: (
        isPc ?
          <>
            {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify Start */}
            <ValidatableDatePicker
              name="requestDateFrom"
              onChange={
                (newValue) => setSearchInputCondition({ ...searchInputCondition, "requestDateFrom": newValue !== null ? newValue.format('YYYY/MM/DD') : null })
              }
              width="100%"
              value={searchInputCondition.requestDateFrom ? dayjs(searchInputCondition.requestDateFrom) : null}
              control={control}
              formState={formState}
              rules={{
                ...validationRules.date(`${controlSettings.LabelList.lblPostDateLabel} From`),
              }}
            />
            {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify End */}
            <MyMLabel sx={{ lineHeight: "40px", padding: "0 15px" }}>~</MyMLabel>
            {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify Start */}
            <ValidatableDatePicker
              name="requestDateTo"
              onChange={
                (newValue) => setSearchInputCondition({ ...searchInputCondition, "requestDateTo": newValue !== null ? newValue.format('YYYY/MM/DD') : null })
              }
              width="100%"
              value={searchInputCondition.requestDateTo ? dayjs(searchInputCondition.requestDateTo) : null}
              control={control}
              formState={formState}
              rules={{
                ...validationRules.date(`${controlSettings.LabelList.lblPostDateLabel} To`),
              }}
            />
            {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify End */}
          </>
          :
          <>
            <Grid container>
              <Grid item xs={4.5}>
                {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify Start */}
                <ValidatableDatePicker
                  name="requestDateFrom"
                  onChange={
                    (newValue) => setSearchInputCondition({ ...searchInputCondition, "requestDateFrom": newValue !== null ? newValue.format('YYYY/MM/DD') : null })
                  }
                  width="100%"
                  value={searchInputCondition.requestDateFrom ? dayjs(searchInputCondition.requestDateFrom) : null}
                  control={control}
                  formState={formState}
                  rules={{
                    ...validationRules.date(`${controlSettings.LabelList.lblPostDateLabel} From`),
                  }}
                />
                {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify End */}
              </Grid>
              <Grid item >
                <MyMLabel sx={{ lineHeight: "40px", padding: "0 20px" }}>~</MyMLabel>
              </Grid>
              <Grid item xs={4.5}>
                {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify Start */}
                <ValidatableDatePicker
                  name="requestDateTo"
                  onChange={
                    (newValue) => setSearchInputCondition({ ...searchInputCondition, "requestDateTo": newValue !== null ? newValue.format('YYYY/MM/DD') : null })
                  }
                  width="100%"
                  value={searchInputCondition.requestDateTo ? dayjs(searchInputCondition.requestDateTo) : null}
                  control={control}
                  formState={formState}
                  rules={{
                    ...validationRules.date(`${controlSettings.LabelList.lblPostDateLabel} To`),
                  }}
                />
                {/* 2026.6.9 ブラウザ言語による日付選択時のエラー対応 Modify End */}
              </Grid>
            </Grid>
          </>
      ),
    },
    {
      xl: 3.5,
      control:
        <Box sx={{ width: '100%', '.MuiFormGroup-root': { display: 'flex', justifyContent: { xl: 'center', md: 'start' } } }}>
          <ValidatableCheckBoxList
            controllerName='checkboxList_AnswerStatus'
            keyValueList={searchCondition.RequestAnswerStatusList}
            checkedList={searchInputCondition.answerStatus}
            onChange={((e, item) => {
              setSearchInputCondition({
                ...searchInputCondition,
                answerStatus:
                  e.target.checked
                    ? [...searchInputCondition.answerStatus, item.Key]
                    : searchInputCondition.answerStatus.filter(x => x !== item.Key)
              })
            })}
            control={control}
            rules={{
              ...validationRules.listRequired(searchInputCondition.answerStatus),
            }}
            formState={formState}
            trigger={trigger}
          />
        </Box>,
    },
    {
      xl: 4,
      label: controlSettings.LabelList.lblTargetProductCategoryLabel,
      control:
        <MyMDropDownList
          sx={{ width: '100%' }}
          options={searchCondition.TargetProductCategoryList}
          needsBlank={true}
          name="targetProductCategory"
          value={searchInputCondition.targetProductCategory ?? ''}
          onChange={(e) =>
            setSearchInputCondition({ ...searchInputCondition, [e.target.name]: e.target.value })
          }
        />,
    },
    {
      xl: 4.5,
      label: controlSettings.LabelList.lblRequestCategoryLabel,
      control:
        <MyMDropDownList
          sx={{ width: '100%' }}
          options={searchCondition.RequestCategoryList}
          needsBlank={true}
          name="requestCategory"
          value={searchInputCondition.requestCategory ?? ''}
          onChange={(e) => setSearchInputCondition({ ...searchInputCondition, [e.target.name]: e.target.value })}
        />,
    },

  ];

  return (
    <Box sx={{ borderBottom: "3px solid #333", padding: "15px 20px", margin: "0px 20px" }}>
      <MyMVariantLabel variant="h6">{controlSettings.LabelList.lblRequestHistoryLabel}</MyMVariantLabel>
      <form onSubmit={handleSubmit(handleSearch)}>
        <Grid container columnSpacing={5} className='pt-2' >
          {conditionControlList.map((item, index) => (
            isPc ?
              <Grid item xs={12} lg={item.lg ?? 6} xl={item.xl} className='d-flex pb-2' sx={{ marginBottom: "7px" }} key={index}>
                {item.label &&
                  <MyMLabel sx={{ minWidth: { xl: index % 3 === 0 ? 190 : 200, xs: 200 }, lineHeight: "40px" }}>
                    {item.label}
                  </MyMLabel>
                }
                {item.control}
              </Grid>
              :
              <Grid item xs={12} lg={item.lg ?? 6} xl={item.xl} className='d-flex pb-2' sx={{ marginBottom: "7px" }} key={index}>
                <Grid container>
                  <Grid item xs={12}>
                    {item.label &&
                      <MyMLabel sx={{ minWidth: { xl: index % 3 === 0 ? 140 : 200, xs: 200 }, lineHeight: "40px" }}>
                        {item.label}
                      </MyMLabel>
                    }
                  </Grid>
                  {item.control}
                </Grid>
              </Grid>
          ))}
          {/* 検索ボタンとクリアボタン */}
          <Grid item xl={3.5} lg={6} xs={12} className='d-flex align-items-end justify-content-end pb-2'>
            <Stack direction='row' marginRight={isPc && '1rem'} spacing={3}>
              {/* 検索ボタン */}
              <MyMSearchButton handleSearch={handleSubmit(handleSearch, onError)} searchText={controlSettings.LabelList.btnSearch} />
              {/* クリアボタン */}
              <MyMButton onClick={handleClear}>{controlSettings.LabelList.btnClear}</MyMButton>
              <MyMButton onClick={handleDownload}>{controlSettings.LabelList.btnDownload}</MyMButton>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
});